import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, MapPin, Tag, Wrench, TrendingDown, ShieldCheck, Smartphone, ArrowRight, Store } from "lucide-react";

export default function Landing() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [cats, setCats] = useState([]);
  const [promos, setPromos] = useState([]);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/promotions").then((r) => setPromos(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const go = (e) => { e.preventDefault(); navigate(`/recherche?q=${encodeURIComponent(q)}`); };

  return (
    <div>
      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/19661361/pexels-photo-19661361.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="marché africain" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-secondary/70" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-28 lg:grid lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-8 animate-fade-up">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-4">
              <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />Afrique francophone
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.05] tracking-tight">
              Les <span className="text-primary">meilleurs prix</span><br />près de chez vous.
            </h1>
            <p className="mt-5 text-lg text-white/80 max-w-xl">
              Comparez les prix entre commerçants, dénichez les bons plans et réservez des prestataires de confiance. Payez par Mobile Money.
            </p>
            <form onSubmit={go} className="mt-8 max-w-2xl bg-white rounded-xl p-2 flex items-center gap-2 shadow-2xl">
              <Search className="w-5 h-5 text-muted-foreground ml-2 shrink-0" />
              <Input data-testid="hero-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Rechercher un produit (riz, smartphone…)"
                className="border-0 focus-visible:ring-0 text-base shadow-none" />
              <Button data-testid="hero-search-submit" type="submit" className="bg-primary hover:bg-primary/90 rounded-lg shrink-0">
                Comparer
              </Button>
            </form>
            <div className="mt-4 flex flex-wrap gap-2">
              {cats.slice(0, 5).map((c) => (
                <button key={c.cat_id} onClick={() => navigate(`/recherche?category=${encodeURIComponent(c.name)}`)}
                  className="text-xs px-3 py-1.5 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors border border-white/20">
                  {c.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="max-w-7xl mx-auto px-6 -mt-14 relative z-10 grid sm:grid-cols-3 gap-4">
        {[
          { icon: TrendingDown, t: "Prix comparés", d: "Le meilleur prix mis en avant automatiquement." },
          { icon: MapPin, t: "Itinéraire", d: "Trouvez la boutique la plus proche sur la carte." },
          { icon: ShieldCheck, t: "Prestataires vérifiés", d: "Devis, RDV et paiement sécurisé." },
        ].map((f, i) => (
          <div key={i} className="bg-card border border-border rounded-lg p-6 hover:-translate-y-1 transition-transform">
            <f.icon className="w-8 h-8 text-primary mb-3" />
            <h3 className="font-display font-bold text-secondary text-lg">{f.t}</h3>
            <p className="text-sm text-muted-foreground mt-1">{f.d}</p>
          </div>
        ))}
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-secondary mb-6">Explorez par catégorie</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {cats.map((c) => (
            <button key={c.cat_id} data-testid={`cat-${c.cat_id}`} onClick={() => navigate(`/recherche?category=${encodeURIComponent(c.name)}`)}
              className="p-5 rounded-lg border border-border bg-card text-center hover:-translate-y-1 hover:border-primary transition-transform">
              <Tag className="w-6 h-6 mx-auto mb-2 text-accent" />
              <span className="text-sm font-medium text-secondary">{c.name}</span>
            </button>
          ))}
        </div>
      </section>

      {/* PROMOS PREVIEW */}
      {promos.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 pb-16">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-secondary">Bons plans du moment</h2>
            <Link to="/promotions"><Button variant="ghost">Tout voir <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {promos.map((p) => (
              <Link key={p.promo_id} to="/promotions" className="group rounded-xl overflow-hidden border border-border bg-card hover:-translate-y-1 transition-transform">
                <div className="relative h-40">
                  {p.image_url ? <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" /> : <div className="w-full h-full bg-muted flex items-center justify-center"><Tag className="w-10 h-10 text-muted-foreground/40" /></div>}
                  <span className="absolute top-3 left-3 bg-primary text-white text-xs font-bold px-2.5 py-1 rounded-md">{p.discount}</span>
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-secondary group-hover:text-primary transition-colors">{p.title}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{p.description}</p>
                  <p className="text-xs text-accent font-medium mt-2">{p.shop_name} · {p.city}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* PRO CTA */}
      <section className="bg-secondary">
        <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-display text-3xl font-bold text-white">Vous êtes commerçant ou prestataire ?</h2>
            <p className="text-white/70 mt-3">Créez votre boutique ou profil, publiez vos prix et promos, recevez des demandes et encaissez par Mobile Money.</p>
            <div className="mt-6 flex gap-3">
              <Link to="/register"><Button className="bg-primary hover:bg-primary/90"><Store className="w-4 h-4 mr-2" />Créer ma boutique</Button></Link>
              <Link to="/register"><Button variant="outline" className="border-white/30 text-white bg-transparent hover:bg-white/10"><Wrench className="w-4 h-4 mr-2" />Devenir prestataire</Button></Link>
            </div>
          </div>
          <div className="flex items-center gap-4 justify-center">
            <Smartphone className="w-16 h-16 text-primary" />
            <div>
              <div className="font-display text-2xl font-bold text-white">Mobile Money</div>
              <p className="text-white/60 text-sm">Orange · MTN · Wave · Moov</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
